import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Minus,
  Plus,
  Ruler,
  ShoppingBag,
} from "lucide-react";

import { getProductClientById, ProductDetailDto } from "../api/productApi";
import { useCart } from "../contexts/CartProvider";
import { gaViewItem, gaAddToCart } from "../analytics/ga";

/* ======================
   HELPERS
====================== */
function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function formatVnd(v?: number) {
  if (v == null) return "—";
  return v.toLocaleString("vi-VN") + "₫";
}

function apiBase() {
  const raw = process.env.REACT_APP_API_URL || "";
  return raw.replace(/\/+$/, "");
}

function resolveImg(url?: string | null) {
  if (!url) return "/images/placeholder.jpg";
  if (/^https?:\/\//i.test(url)) return url;

  const base = apiBase();
  if (!base) return url;

  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

type SafeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
};

function SafeImg({ src, ...props }: SafeImgProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  const normalized =
    src && src !== "/images" && src !== "/images/"
      ? src
      : "/images/placeholder.jpg";

  return (
    <img
      {...props}
      src={failed ? "/images/placeholder.jpg" : normalized}
      loading={props.loading ?? "lazy"}
      decoding={props.decoding ?? "async"}
      onError={() => setFailed(true)}
    />
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
      {children}
    </p>
  );
}

function ChoicePill(props: {
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        "inline-flex min-h-[40px] items-center justify-center rounded-full border px-4 text-[11px] font-medium uppercase tracking-[0.14em] transition",
        props.active
          ? "border-brand-brown bg-brand-brown text-brand-ivory shadow-[0_10px_24px_rgba(43,31,26,0.16)]"
          : "border-brand-brown/12 bg-white/78 text-brand-brown/72 hover:border-brand-brown/24 hover:bg-white hover:text-brand-brown",
        props.disabled && "cursor-not-allowed opacity-35",
      )}
    >
      {props.children}
    </button>
  );
}

/* ======================
   PAGE
====================== */
export default function ProductDetail() {
  const { t, i18n } = useTranslation();

  const isVN = i18n.language?.startsWith("vi");
  const text = {
    brand: "OWL DREAM",
    product: isVN ? "Sản phẩm" : "Product",
    price: isVN ? "Giá" : "Price",
    color: isVN ? "Màu" : "Color",
    size: isVN ? "Size" : "Size",
    quantity: isVN ? "Số lượng" : "Quantity",
    description: isVN ? "Mô tả" : "Description",
    addToCart: isVN ? "Thêm vào giỏ" : "Add to cart",
    buyNow: isVN ? "Mua ngay" : "Buy now",
    chooseVariant: isVN
      ? "Vui lòng chọn màu và size."
      : "Please choose color and size.",
    chooseColorFirst: isVN
      ? "Chọn màu trước để hiện size."
      : "Choose a color first.",
    noSizeAvailable: isVN
      ? "Màu này hiện không còn size."
      : "This color has no available size.",
    outOfStockVariants: isVN
      ? "Hiện không còn biến thể còn hàng."
      : "No available variants in stock.",
    stock: isVN ? "Tồn kho" : "Stock",
    sizeGuide: isVN ? "Bảng size" : "Size guide",
    view: isVN ? "Xem" : "View",
    prev: isVN ? "Trước" : "Prev",
    next: isVN ? "Sau" : "Next",
    outfitNote: isVN
      ? "Thiết kế tối giản, form rõ, tập trung vào sản phẩm."
      : "Minimal design, clean fit, product-first presentation.",
    lookbook: isVN ? "Lookbook" : "Lookbook",
    born: isVN ? "Sinh ra từ màn đêm" : "Born from the night",
    close: isVN ? "Đóng" : "Close",
    intro:
      dataIntroFallback(isVN),
    invalidId: isVN ? "ID sản phẩm không hợp lệ." : "Invalid product id.",
    loginNeed: isVN
      ? "Bạn cần đăng nhập để xem sản phẩm này."
      : "You need to sign in to view this product.",
    forbidden: isVN
      ? "Bạn không có quyền xem sản phẩm này."
      : "You do not have permission to view this product.",
    failed: isVN ? "Không tải được sản phẩm." : "Failed to load product.",
    loading: isVN ? "Đang tải sản phẩm..." : "Loading product...",
    adding: isVN ? "Đang thêm..." : "Adding...",
  };

  const params = useParams<{ id?: string; slug?: string }>();
  const id = params.id ?? params.slug;

  const [data, setData] = useState<ProductDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [qty, setQty] = useState(1);

  const [activeIndex, setActiveIndex] = useState(0);
  const [adding, setAdding] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  const lookbookRef = useRef<HTMLDivElement | null>(null);
  const [showLookbook, setShowLookbook] = useState(false);

  const { addItem, open: openCart } = useCart();

  useEffect(() => {
    const el = lookbookRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShowLookbook(true);
          io.disconnect();
        }
      },
      { rootMargin: "280px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const pid = Number(id);
    if (!pid || Number.isNaN(pid)) {
      setData(null);
      setLoading(false);
      setError(text.invalidId);
      return;
    }

    const ac = new AbortController();

    setLoading(true);
    setError(null);

    getProductClientById(pid, ac.signal)
      .then((res) => {
        setData(res);
        setSelectedColor(null);
        setSelectedSize(null);
        setQty(1);
        setActiveIndex(0);
      })
      .catch((e: any) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

        if (e?.response?.status === 401) {
          setError(text.loginNeed);
        } else if (e?.response?.status === 403) {
          setError(text.forbidden);
        } else {
          setError(e?.message || text.failed);
        }

        setData(null);
      })
      .finally(() => setLoading(false));

    return () => ac.abort();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data) return;

    const price = Number(data.salePrice ?? data.basePrice ?? 0);

    gaViewItem({
      currency: "VND",
      value: price,
      item: {
        item_id: String(data.id),
        item_name: String(data.name),
        price,
        item_brand: "OWL DREAM",
        item_category: "Product",
      },
    });
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showSizeGuide) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowSizeGuide(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSizeGuide]);

  const images = useMemo(() => data?.images ?? [], [data]);

  const colors = useMemo(() => {
    if (!data) return [];
    return Array.from(
      new Set(
        data.variants.filter((v) => v.stockQuantity > 0).map((v) => v.color),
      ),
    );
  }, [data]);

  const sizesByColor = useMemo(() => {
    if (!data || !selectedColor) return [];
    return Array.from(
      new Set(
        data.variants
          .filter((v) => v.color === selectedColor && v.stockQuantity > 0)
          .map((v) => v.size),
      ),
    );
  }, [data, selectedColor]);

  const selectedVariant = useMemo(() => {
    if (!data || !selectedColor || !selectedSize) return undefined;
    return data.variants.find(
      (v) => v.color === selectedColor && v.size === selectedSize,
    );
  }, [data, selectedColor, selectedSize]);

  const price = data?.salePrice ?? data?.basePrice;
  const stock = selectedVariant?.stockQuantity;

  const canSelect = Boolean(selectedVariant);

  const lookbookItems = useMemo(() => {
    const fromProduct = (data?.images ?? []).slice(0, 8).map((img, i) => ({
      id: img.id ?? `p-${i}`,
      imageUrl: resolveImg(img.imageUrl),
      href: "#",
    }));

    if (fromProduct.length > 0) return fromProduct;

    return Array.from({ length: 8 }).map((_, i) => ({
      id: `ph-${i}`,
      imageUrl: "/images/placeholder.jpg",
      href: "#",
    }));
  }, [data]);

  const handleAddToCart = async () => {
    if (!data || !selectedVariant) return;

    try {
      setAdding(true);

      await addItem({
        productId: data.id,
        color: selectedColor!,
        size: selectedSize!,
        quantity: qty,
      });

      const vPrice = Number(data.salePrice ?? data.basePrice ?? 0);

      gaAddToCart({
        currency: "VND",
        value: vPrice * qty,
        quantity: qty,
        item: {
          item_id: String(data.id),
          item_name: String(data.name),
          price: vPrice,
          item_brand: "OWL DREAM",
          item_category: "Product",
          item_variant: `${selectedColor}-${selectedSize}`,
        },
      });

      openCart();
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6efe4] pt-24">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-6">
          <div className="animate-pulse">
            <div className="mb-8 h-4 w-40 rounded bg-brand-brown/10" />
            <div className="grid gap-8 lg:grid-cols-[1.08fr_0.92fr] xl:gap-12">
              <div className="aspect-[4/5] rounded-[28px] border border-brand-brown/10 bg-brand-brown/5" />
              <div className="rounded-[28px] border border-brand-brown/10 bg-white/70 p-8">
                <div className="h-4 w-24 rounded bg-brand-brown/10" />
                <div className="mt-5 h-10 w-2/3 rounded bg-brand-brown/10" />
                <div className="mt-8 h-12 w-full rounded bg-brand-brown/10" />
                <div className="mt-4 h-12 w-full rounded bg-brand-brown/10" />
                <div className="mt-4 h-12 w-full rounded bg-brand-brown/10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6efe4] pt-24">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-6">
          <div className="rounded-[24px] border border-red-500/20 bg-red-500/5 p-6">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-red-700/80">
              {text.loading}
            </p>
            <p className="mt-3 text-sm text-red-700/80">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const heroImage = resolveImg(images[activeIndex]?.imageUrl);

  return (
    <>
      <div className="min-h-screen bg-[#f6efe4] pb-24 pt-24 text-brand-brown">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-6">
          <div className="mb-6 flex items-center justify-between gap-4">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
              {text.brand} / {text.product}
            </p>

            {images.length > 1 && (
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/34">
                {activeIndex + 1} / {images.length}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.08fr_0.92fr] xl:gap-12">
            {/* GALLERY */}
            <div>
              <div className="overflow-hidden rounded-[30px] border border-brand-brown/10 bg-white/62 shadow-[0_24px_80px_rgba(43,31,26,0.06)]">
                <div className="relative aspect-[4/5] bg-brand-brown/[0.03] sm:aspect-[4/4.8] lg:aspect-[4/5]">
                  <SafeImg
                    src={heroImage}
                    alt={data.name}
                    className="h-full w-full object-contain"
                    draggable={false}
                  />

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/[0.03]" />

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setActiveIndex((x) => Math.max(0, x - 1))}
                        disabled={activeIndex === 0}
                        className="absolute left-4 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-brand-brown/10 bg-white/80 text-brand-brown/70 transition hover:bg-white hover:text-brand-brown disabled:opacity-30"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setActiveIndex((x) => Math.min(images.length - 1, x + 1))
                        }
                        disabled={activeIndex === images.length - 1}
                        className="absolute right-4 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-brand-brown/10 bg-white/80 text-brand-brown/70 transition hover:bg-white hover:text-brand-brown disabled:opacity-30"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {images.length > 1 && (
                <div className="mt-4 grid grid-cols-5 gap-3 sm:grid-cols-6 lg:grid-cols-5 xl:grid-cols-6">
                  {images.slice(0, 8).map((img, i) => {
                    const url = resolveImg(img.imageUrl);
                    const active = i === activeIndex;

                    return (
                      <button
                        key={img.id ?? i}
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        className={cn(
                          "overflow-hidden rounded-[18px] border bg-white/70 transition",
                          active
                            ? "border-brand-brown shadow-[0_10px_24px_rgba(43,31,26,0.10)]"
                            : "border-brand-brown/10 hover:border-brand-brown/24",
                        )}
                      >
                        <div className="aspect-[4/5] bg-brand-brown/[0.03]">
                          <SafeImg
                            src={url}
                            alt=""
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* INFO */}
            <div className="lg:sticky lg:top-28">
              <div className="overflow-hidden rounded-[30px] border border-brand-brown/10 bg-white/72 shadow-[0_24px_80px_rgba(43,31,26,0.06)]">
                <div className="p-6 sm:p-8 lg:p-9">
                  <SectionLabel>{text.brand}</SectionLabel>

                  <h1 className="mt-4 text-[26px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[34px] lg:text-[40px]">
                    {data.name}
                  </h1>

                  <p className="mt-4 text-sm leading-7 text-brand-brown/58">
                    {text.outfitNote}
                  </p>

                  <div className="mt-7 flex items-end justify-between gap-5 border-t border-brand-brown/8 pt-6">
                    <SectionLabel>{text.price}</SectionLabel>
                    <div className="text-right">
                      {data.salePrice != null && (
                        <p className="text-sm text-brand-brown/36 line-through">
                          {formatVnd(data.basePrice)}
                        </p>
                      )}
                      <p className="text-[22px] font-medium tracking-[0.01em] text-brand-brown">
                        {formatVnd(price)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-7 space-y-6">
                    <div>
                      <div className="flex items-center justify-between gap-4">
                        <SectionLabel>{text.color}</SectionLabel>
                        {selectedColor && (
                          <p className="text-[11px] uppercase tracking-[0.14em] text-brand-brown/48">
                            {selectedColor}
                          </p>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2.5">
                        {colors.map((c) => (
                          <ChoicePill
                            key={c}
                            active={c === selectedColor}
                            onClick={() => {
                              setSelectedColor(c);
                              setSelectedSize(null);
                              setQty(1);
                            }}
                          >
                            {c}
                          </ChoicePill>
                        ))}

                        {colors.length === 0 && (
                          <p className="text-sm text-brand-brown/50">
                            {text.outOfStockVariants}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-4">
                        <SectionLabel>{text.size}</SectionLabel>

                        <button
                          type="button"
                          onClick={() => setShowSizeGuide(true)}
                          className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/48 transition hover:text-brand-brown"
                        >
                          <Ruler className="h-4 w-4" />
                          {text.sizeGuide}
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2.5">
                        {!selectedColor ? (
                          <p className="text-sm text-brand-brown/50">
                            {text.chooseColorFirst}
                          </p>
                        ) : sizesByColor.length === 0 ? (
                          <p className="text-sm text-brand-brown/50">
                            {text.noSizeAvailable}
                          </p>
                        ) : (
                          sizesByColor.map((s) => (
                            <ChoicePill
                              key={s}
                              active={s === selectedSize}
                              onClick={() => setSelectedSize(s)}
                            >
                              {s}
                            </ChoicePill>
                          ))
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-4">
                        <SectionLabel>{text.quantity}</SectionLabel>
                        {typeof stock === "number" && (
                          <p className="text-[11px] uppercase tracking-[0.14em] text-brand-brown/48">
                            {text.stock}: {stock}
                          </p>
                        )}
                      </div>

                      <div className="mt-3 inline-flex items-center overflow-hidden rounded-full border border-brand-brown/12 bg-white/80">
                        <button
                          type="button"
                          onClick={() => setQty((q) => Math.max(1, q - 1))}
                          className="inline-flex h-11 w-12 items-center justify-center text-brand-brown/72 transition hover:bg-brand-brown/5 hover:text-brand-brown"
                        >
                          <Minus className="h-4 w-4" />
                        </button>

                        <div className="grid h-11 w-12 place-items-center text-sm font-medium text-brand-brown">
                          {qty}
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setQty((q) =>
                              typeof stock === "number" && stock > 0
                                ? Math.min(stock, q + 1)
                                : q + 1,
                            )
                          }
                          className="inline-flex h-11 w-12 items-center justify-center text-brand-brown/72 transition hover:bg-brand-brown/5 hover:text-brand-brown"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      disabled={!canSelect || adding}
                      onClick={handleAddToCart}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-brand-brown px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-92 disabled:opacity-35"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      {adding ? text.adding : text.addToCart}
                    </button>

                    <button
                      type="button"
                      disabled={!canSelect}
                      className="inline-flex h-12 items-center justify-center rounded-full border border-brand-brown bg-white px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:bg-brand-brown hover:text-brand-ivory disabled:opacity-35"
                    >
                      {text.buyNow}
                    </button>
                  </div>

                  {!canSelect && (
                    <p className="mt-3 text-sm text-brand-brown/50">
                      {text.chooseVariant}
                    </p>
                  )}

                  <div className="mt-8 border-t border-brand-brown/8 pt-7">
                    <SectionLabel>{text.description}</SectionLabel>
                    <p className="mt-3 text-sm leading-7 text-brand-brown/60">
                      {data.description || text.intro}
                    </p>
                  </div>

                  <div className="mt-8 flex items-center gap-4">
                    <div className="h-px flex-1 bg-brand-brown/10" />
                    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/34">
                      {text.born}
                    </p>
                    <div className="h-px flex-1 bg-brand-brown/10" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={lookbookRef} className="mt-20 sm:mt-24">
            <div className="mb-8 flex items-center gap-5">
              <div className="h-px flex-1 bg-brand-brown/10" />
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                {text.lookbook}
              </p>
              <div className="h-px flex-1 bg-brand-brown/10" />
            </div>

            {!showLookbook ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square animate-pulse rounded-[22px] border border-brand-brown/10 bg-brand-brown/[0.03]"
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {lookbookItems.map((it) => (
                  <a
                    key={it.id}
                    href={it.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group overflow-hidden rounded-[22px] border border-brand-brown/10 bg-white/70 transition hover:border-brand-brown/22 hover:shadow-[0_18px_40px_rgba(43,31,26,0.08)]"
                  >
                    <div className="aspect-square bg-brand-brown/[0.03]">
                      <SafeImg
                        src={it.imageUrl}
                        alt=""
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        draggable={false}
                      />
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showSizeGuide && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSizeGuide(false)}
          />

          <div
            role="dialog"
            aria-modal="true"
            className="absolute inset-x-0 top-1/2 mx-auto w-[92vw] max-w-3xl -translate-y-1/2"
          >
            <div className="overflow-hidden rounded-[28px] border border-brand-brown/12 bg-white shadow-[0_30px_90px_rgba(0,0,0,0.16)]">
              <div className="flex items-start justify-between gap-4 border-b border-brand-brown/10 px-6 py-5 sm:px-8">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                    {text.brand}
                  </p>
                  <h3 className="mt-2 text-[18px] font-medium tracking-[0.01em] text-brand-brown">
                    {text.sizeGuide}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={() => setShowSizeGuide(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white text-brand-brown/70 transition hover:border-brand-brown/20 hover:text-brand-brown"
                  aria-label={text.close}
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-6 sm:px-8">
                <p className="text-sm leading-6 text-brand-brown/56">
                  {t(
                    "product.sizeGuide.intro",
                    isVN
                      ? "Số đo theo áo thành phẩm (cm), không phải số đo cơ thể."
                      : "Measurements are garment size (cm), not body measurements.",
                  )}
                </p>

                <div className="mt-5 overflow-x-auto rounded-[18px] border border-brand-brown/10">
                  <table className="min-w-[760px] w-full text-sm">
                    <thead className="bg-brand-brown/[0.03]">
                      <tr>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Size
                        </th>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Chest
                        </th>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Length
                        </th>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Shoulder
                        </th>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Sleeve
                        </th>
                        <th className="p-3 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                          Height
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {[
                        ["M", "55", "69", "52", "60", "160–172cm"],
                        ["L", "58", "72", "54", "62", "168–178cm"],
                        ["XL", "61", "75", "56", "64", "175–185cm"],
                      ].map((row) => (
                        <tr key={row[0]} className="border-t border-brand-brown/10">
                          {row.map((cell, i) => (
                            <td
                              key={i}
                              className={cn(
                                "p-3",
                                i === 5 && "text-brand-brown/52",
                              )}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[20px] border border-brand-brown/10 bg-brand-ivory/78 p-4">
                    <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                      {isVN ? "Fit" : "Fit"}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-brand-brown/58">
                      {isVN
                        ? "Form rộng nhẹ, lên dáng clean và dễ mặc hằng ngày."
                        : "Relaxed fit with a clean everyday silhouette."}
                    </p>
                  </div>

                  <div className="rounded-[20px] border border-brand-brown/10 bg-brand-ivory/78 p-4">
                    <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                      {isVN ? "Lưu ý" : "Note"}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-brand-brown/58">
                      {isVN
                        ? "Nếu thích oversize hơn, nên lên thêm 1 size."
                        : "Size up once for a more oversized look."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function dataIntroFallback(isVN: boolean) {
  return isVN
    ? "Thiết kế tối giản, form thoải mái, tập trung vào chất liệu và tổng thể mặc lên người."
    : "Minimal design with a relaxed silhouette, focused on material, proportion, and clean presence.";
}